import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { DevicesService } from '../../services/graphQl/devices/devices.service';
import { DeviceCheckboxFilter, DeviceFilter } from '../../models/devices';
import { DrawerModule } from 'primeng/drawer';
import { AccordionModule } from 'primeng/accordion';
import { CheckboxModule } from 'primeng/checkbox';

@Component({
    selector: 'app-devices-filter',
    standalone : true,
    imports: [ReactiveFormsModule, DrawerModule, AccordionModule, CheckboxModule],
    templateUrl: './devices-filter.component.html',
    styleUrl: './devices-filter.component.css'
})
export class DevicesFilterComponent {
  private destroy$ = new Subject<void>();
  
  @Input() visible = false;
  
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() closeFilter = new EventEmitter<void>();
  @Output() clearFilter = new EventEmitter<void>();

  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private devicesService: DevicesService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      estado: this.fb.group({
        todos: false,
        habilitado: false,
        deshabilitado: false,
      }),
      tipo: this.fb.group({
        todos: false,
        smartPOS: false,
        softPOS: false,
      }),
      comercio: this.emptyGroup(),
      dispositivo: this.emptyGroup()
    });

    this.checkCurrentFilter();
    this.setupAllWatchers();

    this.form.valueChanges.subscribe(value => {
      //se agrega timeout, para darle tiempo de procesar el "todos", y obtener los valores "actuales"
      setTimeout(() => {
        const deviceFilter: DeviceFilter = this.generateDeviceFilter();
        this.devicesService.setDeviceFilter(deviceFilter);
      }, 50);
    })

    this.devicesService.deviceCheckboxFilter$.pipe(takeUntil(this.destroy$)).subscribe((deviceCheckboxFilter: DeviceCheckboxFilter) => {
      this.addDynamicItemsGroup(
        [...deviceCheckboxFilter.merchants],
        [...deviceCheckboxFilter.devices]
      );
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get comercioControls(): string[] {
    const group = this.form.get('comercio') as FormGroup;
    const controls = Object.keys(group.controls).filter(key => key !== 'todos');
    return controls;
  }

  get dispositivoControls(): string[] {
    const group = this.form.get('dispositivo') as FormGroup;
    const controls = Object.keys(group.controls).filter(key => key !== 'todos');
    return controls;
  }

  emptyGroup() {
    return this.fb.group({
        todos: false,
    });
  }

  addDynamicItemsGroup(merchants: string[], devices: string[]) {
    this.form.reset({
      comercio: this.emptyGroup(),
      dispositivo: this.emptyGroup()
    }, { emitEvent: false });

    const groupComercio = this.form.get('comercio') as FormGroup;
    const groupDispositivo = this.form.get('dispositivo') as FormGroup;
    
    merchants.forEach(item => {
      groupComercio.addControl(item, this.fb.control(false), { emitEvent: false });
    });

    devices.forEach(item => {
      groupDispositivo.addControl(item, this.fb.control(false), { emitEvent: false });
    });

    this.setupGroupWatcher(groupComercio);
    this.setupGroupWatcher(groupDispositivo);

    this.checkCurrentFilter();
  }

  close() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.closeFilter.emit();
  }

  public clear() {
    this.devicesService.clearFilter();
    this.clearFilter.emit();
    this.checkCurrentFilter();
  }

  setupAllWatchers() {
    const estadoGroup = this.form.get('estado') as FormGroup;
    const tipoGroup = this.form.get('tipo') as FormGroup;

    this.setupGroupWatcher(estadoGroup);
    this.setupGroupWatcher(tipoGroup);
  }

  setupGroupWatcher(group: FormGroup<any>) {
    group.valueChanges.subscribe(value => {
      const { todos, ...resto } = value;

       // Si el usuario cambió "Todos"
      if (group.get('todos')?.dirty) {
        setTimeout(() => {
          // se le agrega 20ms, xq al tener campos "dinamicos" en el group, lanza 2 veces el "todos", con el timeout solo lo hace 1 vez
          Object.keys(resto).forEach(key =>
            group.get(key)?.setValue(todos, { emitEvent: false })
          );
          group.get('todos')?.markAsPristine(); // limpiamos dirty
        }, 20);
      } else {
        // Si cambió un hijo, actualizamos "Todos"
        const allChecked = Object.values(resto).every(v => v === true);
        group.get('todos')?.setValue(allChecked, { emitEvent: false });
      }
    });
  }

  generateDeviceFilter(): DeviceFilter {
    const deviceFilter: DeviceFilter = {};

    const { estado, tipo, comercio, dispositivo } = this.form.value;

    deviceFilter.checkboxes = {
      status: estado,
      type: tipo,
      merchants: comercio,
      devices: dispositivo
    }

    return deviceFilter;
  }

  checkCurrentFilter() {
    const { status, type, merchants, devices } = this.devicesService.currentFilter?.checkboxes ?? {};

    const statusGroup = this.form.get('estado') as FormGroup;
    const typeGroup = this.form.get('tipo') as FormGroup;
    const merchantsGroup = this.form.get('comercio') as FormGroup;
    const devicesGroup = this.form.get('dispositivo') as FormGroup;

    if(status) {
      statusGroup.patchValue({
        ...status
      }, { emitEvent: false });
    } else {
      // si no hay un status previo, marcamos todo, es la primera interacción con el filtro
      statusGroup.patchValue({
        todos: true,
        habilitado: true,
        deshabilitado: true,
      }, { emitEvent: false });
    }

    if(type) {
      typeGroup.patchValue({
        ...type
      }, { emitEvent: false });
    } else {
      // si no hay un type previo, marcamos todo, es la primera interacción con el filtro
      typeGroup.patchValue({
        todos: true,
        smartPOS: true,
        softPOS: true,
      }, { emitEvent: false });
    }

    if(merchants) {
      //marcamos solo los seleccionados
      Object.keys(merchantsGroup.controls).forEach(key => {
        merchantsGroup.get(key)?.setValue(merchants[key] ?? false, { emitEvent: false });
      });
    } else {
      // si no hay una seleccion previa, marcamos todo, es la primera interacción con el filtro
      Object.keys(merchantsGroup.controls).forEach(key => {
        merchantsGroup.get(key)?.setValue(true, { emitEvent: false });
      });
    }

    if(devices) {
      //marcamos solo los seleccionados
      Object.keys(devicesGroup.controls).forEach(key => {
        devicesGroup.get(key)?.setValue(devices[key] ?? false, { emitEvent: false });
      });
    } else {
      // si no hay una seleccion previa, marcamos todo, es la primera interacción con el filtro
      Object.keys(devicesGroup.controls).forEach(key => {
        devicesGroup.get(key)?.setValue(true, { emitEvent: false });
      });
    }

  }
}
